import { expandFieldsBetween } from './tourTargets';

/**
 * Guided tours. Each step highlights one element (found by its visible label,
 * button text or placeholder — see tourTargets.js) and explains it. Steps
 * whose element is not on screen are skipped, so one tour works for every
 * role and product. A step with `expand` is replaced at run time by one step
 * per element it finds.
 */
const TOURS = {
  'raise-ticket': {
    title: 'Raise a new ticket',
    route: '/dashboard/tickets/new',
    steps: [
      {
        title: 'The New Ticket form',
        body: 'This tour walks through every part of the form. Use Next and Back, or the arrow keys. You can try the fields while the tour is open — nothing is submitted unless you click Create Ticket yourself.',
      },
      {
        target: { selector: '.tk-form-steps' },
        title: 'Progress',
        body: 'Shows where you are: 1 Domain, 2 Details, 3 Submit. It moves forward as you fill in the form.',
      },
      {
        target: { label: 'Domain' },
        title: 'Domain (required)',
        body: 'The kind of problem. App Support is for issues with a software product; Hardware and Software are internal IT requests. Clients are set to App Support automatically. Changing the domain resets the fields below it.',
      },
      {
        target: { label: 'Priority' },
        title: 'Priority',
        body: 'How urgent it is: Low, Medium (default), High or Critical. Priority sets the target resolution time — Critical is for work stopped for many users, Low for minor issues or questions.',
      },
      {
        target: { label: 'Product' },
        title: 'Product (required)',
        body: 'Choose the product the problem is in. Only products you are allowed to raise tickets for are listed; if you have just one, it is picked for you. The product decides which extra fields appear and which team receives the ticket.',
      },
      {
        expand: () => expandFieldsBetween({
          afterHeading: 'Domain-specific Details',
          untilLabel: 'Subject',
          skipLabels: ['Product'],
        }),
        fallback: {
          target: { heading: 'Domain-specific Details' },
          title: 'Product-specific details',
          body: 'After you choose a product, its own fields appear in this section — for example client, branch or module. Fields marked * are required. Pick a product and restart the tour to have each field explained.',
        },
      },
      {
        target: { label: 'Subject' },
        title: 'Subject (required)',
        body: 'A short one-line summary, e.g. "Unable to print receipts in Collections". The support team sees this first in every list.',
      },
      {
        target: { label: 'Description' },
        title: 'Description (required)',
        body: 'Explain the problem in detail: what you were doing, what happened, any error message, and when it started. The more detail, the faster it is solved.',
      },
      {
        target: { label: 'Ticket Submitted By' },
        title: 'Ticket Submitted By',
        body: 'Filled in automatically with your name and email — updates about the ticket go to you.',
      },
      {
        target: { label: 'Attachments / Screenshots' },
        title: 'Attachments / Screenshots (optional)',
        body: 'Add screenshots or files (images, PDF, Word, Excel, CSV, TXT, LOG). You can pick several; use "remove" to drop one. They upload right after the ticket is created.',
      },
      {
        target: { button: 'Create Ticket' },
        title: 'Create Ticket',
        body: 'Click when everything is filled in. You will see the ticket number (e.g. PR000123), the ticket page opens, and you get a notification and email. Track it later under My Tickets or My Requests.',
      },
    ],
  },

  'my-tickets': {
    title: 'Find and track your tickets',
    route: '/dashboard/tickets/my-tickets',
    steps: [
      {
        target: { selector: '.tk-subnav' },
        title: 'Tickets menu',
        body: 'Switch between the parts of Tickets you can use — New, My Tickets, and (for staff) All Tickets, My Tasks, Queue, Reports and the Actions menu.',
      },
      {
        target: { selectWithOption: 'All products' },
        title: 'Product filter',
        body: 'Limit every Tickets page to one product. Choose "All products" to see everything again.',
      },
      {
        target: { selector: '.tk-segmented' },
        title: 'Status tabs',
        body: 'Filter by status: All Tickets, Open, In Progress, Pending, Completed, Closed, Reopen or Cancelled.',
      },
      {
        target: { placeholder: 'Search your tickets' },
        title: 'Search',
        body: 'Search by ticket number, subject, description, category, status, priority, client or assignee.',
      },
      {
        target: { selector: 'table' },
        title: 'Your tickets',
        body: 'Tickets you created or that are assigned to you. Columns show the assignee ("Unassigned" until someone picks it up), status, priority and created time. Click a ticket number to open its details, comments, attachments and work-time history.',
      },
    ],
  },

  'my-requests': {
    title: 'Use My Requests',
    route: '/dashboard/my-requests',
    steps: [
      {
        target: { button: 'Raise a Request' },
        title: 'Raise a Request',
        body: 'Opens the New Ticket form to report a new issue.',
      },
      {
        target: { placeholder: 'Search your tickets' },
        title: 'Search',
        body: 'Find one of your tickets by number, subject or other details.',
      },
      {
        target: { selector: 'table' },
        title: 'Tickets you raised',
        body: 'Every ticket you raised with its subject, status and due date. Click a ticket number to open it. Use Prev / Next at the bottom to page through older tickets.',
      },
    ],
  },

  'update-status': {
    title: 'Update a ticket status',
    route: '/dashboard/tickets/update-status',
    steps: [
      {
        target: { label: 'Ticket number' },
        title: 'Ticket number',
        body: 'Type or pick the ticket to update. Its current status, subject and description appear below once found.',
      },
      {
        target: { label: 'New Status' },
        title: 'New Status',
        body: 'In progress claims the ticket (it is assigned to you and appears in My Tasks). Pending puts it on hold, Completed records the fix, Cancelled stops it.',
      },
      {
        target: { label: 'Update Reason' },
        title: 'Update Reason',
        body: 'Why the status is changing. Required for Pending and Cancelled. When you choose Completed this becomes "Reason for Cause" (what caused the issue) and a second box "Reason for Resolution" (how it was fixed) appears — both are required.',
      },
      {
        target: { button: 'Update Status' },
        title: 'Update Status',
        body: 'Saves the change. The requester is notified with the new status and reason.',
      },
    ],
  },

  transfer: {
    title: 'Transfer a ticket',
    route: '/dashboard/tickets/transfer',
    steps: [
      {
        target: { label: 'Transferable ticket' },
        title: 'Transferable ticket',
        body: 'Pick the ticket to hand over. Only Open, Pending and In Progress tickets you may transfer are listed; the current assignees are shown once selected.',
      },
      {
        target: { label: 'Transfer to' },
        title: 'Transfer to (select one)',
        body: 'Choose the person who should take over. Their current open ticket count helps you balance the load.',
      },
      {
        target: { label: 'Transfer Reason' },
        title: 'Transfer Reason (optional)',
        body: 'Explain why — the new assignee and the requester can see it.',
      },
      {
        target: { button: 'Transfer Ticket' },
        title: 'Transfer Ticket',
        body: 'Moves the ticket and notifies the requester and the new assignee.',
      },
    ],
  },

  'assign-queue': {
    title: 'Assign tickets from your queue',
    route: '/dashboard/tickets/manager-queue',
    steps: [
      {
        target: { label: 'Your queue' },
        title: 'Your queue',
        body: 'New and reopened tickets waiting for you to assign. Search and select one.',
      },
      {
        target: { label: 'Assign to' },
        title: 'Assign to (select one)',
        body: 'Pick the lead or member to work on it — the number next to each name is their open tickets. You can choose yourself to take it.',
      },
      {
        target: { label: 'Notes' },
        title: 'Notes (optional)',
        body: 'Instructions or context for the assignee.',
      },
      {
        target: { button: 'Assign downstream' },
        title: 'Assign downstream',
        body: 'Assigns the ticket. It appears in the assignee\'s My Tasks and the requester is notified.',
      },
    ],
  },

  profile: {
    title: 'Update your profile',
    route: '/dashboard/profile',
    steps: [
      {
        target: { placeholder: 'Your full name', wrap: true },
        title: 'Full name (required)',
        body: 'Your name as shown on tickets and to the support team.',
      },
      {
        target: { placeholder: 'e.g. Senior Support Engineer', wrap: true },
        title: 'Designation',
        body: 'Your job title. Staff need it to be added to product teams.',
      },
      {
        target: { placeholder: 'e.g. +91', wrap: true },
        title: 'Mobile number',
        body: 'Used for SMS updates about tickets you raise.',
      },
      {
        target: { placeholder: 'e.g. IT Support', wrap: true },
        title: 'Department',
        body: 'Your department or team.',
      },
      {
        target: { placeholder: 'Optional employee', wrap: true },
        title: 'Employee ID (optional)',
        body: 'Your employee or badge number.',
      },
      {
        target: { button: 'Save profile' },
        title: 'Save profile',
        body: 'Saves your changes. Your email cannot be changed here — it comes from your sign-in account.',
      },
    ],
  },

  'getting-around': {
    title: 'Getting around the portal',
    route: '/dashboard',
    steps: [
      {
        target: { selector: '.sidebar', global: true },
        title: 'Sidebar',
        body: 'Every section you can use, grouped (Work, Catalog, Account, Support…). You only see what your role allows. The Help section is always at the bottom.',
      },
      {
        target: { selector: '[data-testid="notifications-button"]', global: true },
        title: 'Notifications',
        body: 'Updates about your tickets. A red badge shows how many are unread. Refresh the page to load new ones.',
      },
      {
        target: { selector: '[data-testid="user-name"]', global: true },
        title: 'Your profile',
        body: 'Click your name to open My Profile.',
      },
      {
        target: { selector: '[data-testid="logout-button"]', global: true },
        title: 'Sign out',
        body: 'Signs you out of the portal.',
      },
    ],
  },
};

export function getTour(id) {
  return TOURS[id] || null;
}

export default TOURS;
